import {
  DocumentViewState,
  DocumentModel,
  BaseDocumentSearchModel,
  RootState,
  DocumentVersionModel,
  RelatedDocumentViewModel,
  WorkflowDocumentSearchModel,
  DocumentPageModel,
  DocumentPageDataModel,
  DocumentActionsModel,
  EnvironmentConstants,
  MStoreExceptionResponseModel,
  DocumentSearchModel
} from '@/types';
import { ActionTree, Dispatch } from 'vuex';
import axios, { CancelTokenSource } from 'axios';
import { DocumentService } from '@/services/DocumentService';
import { WorkflowJobService } from '@/services/WorkflowJobService';
import { DocumentViewUtils } from '@/utils/documentViewUtils';
import { mstoreSettings } from '@/settings/mstoreSettings';
import { Logger } from '@/classes/Logger';
import { getDocumentSearchModel } from './functions';
import { DocumentErrorModel } from '@/types/models/document/DocumentErrorModel';
import ApiConfiguration from '@/classes/ApiConfiguration';

export const actions: ActionTree<DocumentViewState, RootState> = {
  setDocumentSearchModel({ state, commit }, documentSearchModel: BaseDocumentSearchModel) {
    state.ViewConversionId = null;
    commit('DOCUMENT_SEARCH_MODEL', documentSearchModel);
  },
  setDocumentVersionBeingViewed({ state, commit }, version: DocumentVersionModel) {
    state.ViewConversionId = null;
    commit('DOCUMENT_VERSION_BEING_VIEWED', version);
  },
  setRelatedDocumentBeingViewed({ state, commit }, relatedDocument: RelatedDocumentViewModel) {
    state.ViewConversionId = null;
    commit('RELATED_DOCUMENT_BEING_VIEWED', relatedDocument);
    this.dispatch('documentViewStore/loadPage', 1);
  },
  async loadPage({ state, commit, dispatch, rootGetters }, pageNumber: number) {
    commit('PAGE_NUMBER', pageNumber);
    commit('PAGE_LOADING');
    commit('SET_IS_CONVERSION_SLOW', false);
    const documentSearchModel = getDocumentSearchModel(state) as DocumentSearchModel;
    const documentPageData = {
      documentId: documentSearchModel.documentId,
      pageNumber,
      cabinetId: documentSearchModel.cabinetId
    } as DocumentPageDataModel;
    commit('LOAD_PAGE_FROM_CACHE', documentPageData);
    if (rootGetters['documentViewStore/isDocumentPageLoaded'](documentPageData)) {
      commit('PAGE_LOADED_FROM_CACHE');
      this.dispatch('documentViewStore/downloadPageNeighbours', documentPageData);
      return;
    }

    if (documentCancelTokenSource) {
      documentCancelTokenSource.cancel('New request initiated');
    }
    documentCancelTokenSource = axios.CancelToken.source();

    try {
      const document: DocumentModel = ((await ApiConfiguration.apiWithoutNotification().document.documentList(
        {
          // tslint:disable:object-literal-key-quotes
          CabinetId: documentSearchModel.cabinetId,
          DocumentId: documentSearchModel.documentId,
          Page: pageNumber,
          ViewConversionId: state.ViewConversionId ?? undefined,
          'AutoSaveConfiguration.Enabled': documentSearchModel.autoSaveConfiguration?.enabled,
          'AutoSaveConfiguration.Scope': documentSearchModel.autoSaveConfiguration?.scope,
          DocumentPagePreferences: documentSearchModel.documentPagePreferences
          // tslint:enable:object-literal-key-quotes
        },
        { cancelToken: documentCancelTokenSource.token }
      )) as unknown) as DocumentModel;
      commit('SET_CURRENT_VIEW_CONVERSION_ID', document.viewConversionId);
      try {
        if (document.isBeingConverted === true) {
          try {
            await connectToSignalRHub(state, rootGetters, dispatch);
          } catch (error) {
            Logger.$log('failed to connect to SignalR Hub');
            return;
          }
        }
        await handleGetDocumentResponse(document, commit, dispatch, pageNumber);
      } catch (error) {
        handleGetDocumentError(error, commit, document);
      }
    } catch (error) {
      if (!axios.isCancel(error)) {
        const exceptionResponse = error as MStoreExceptionResponseModel;
        commit('DOCUMENT_LOAD_FAILED', exceptionResponse.notification);
      }
      Logger.$log('Failed to get document when loading page', error);
    }
  },
  async deleteDocument({ state, commit, getters, dispatch }, documentModel: BaseDocumentSearchModel) {
    try {
      commit('DELETING', true);

      const model = documentModel || getDocumentSearchModel(state);

      await DocumentService.deleteDocumentAsync(model);

      commit('REMOVE_DOC_PAGES_FROM_CACHE', model.documentId);
      commit('DOCUMENT_DELETED', model.documentId);
      if (model.documentId === getters.document.id) {
        dispatch('unloadDocument');
      }
    } catch (error) {
      Logger.$log('Error deleting document', error);
    } finally {
      commit('DELETING', false);
    }
  },
  removeCachedPagesForDocument({ state, commit, dispatch }, { documentId, pages }) {
    commit('REMOVE_DOC_PAGES_FROM_CACHE', documentId);
    dispatch('thumbnailStore/removeThumbnailPagesForDocument', { documentId, pages }, { root: true });
  },
  async deletePage({ state, getters, commit, dispatch }, { pageNumber, reloadPageOnDelete }) {
    try {
      commit('PAGE_NUMBER', pageNumber);
      commit('DELETING', true);

      const model = getDocumentSearchModel(state);

      await DocumentService.deletePageAsync(model, pageNumber);

      commit('REMOVE_DOC_PAGES_FROM_CACHE', model.documentId);
      commit('UPDATE_PAGE_COUNT', -1);
      commit('PAGE_DELETED', { documentId: model.documentId, pageNumber } as DocumentPageModel);
      dispatch('thumbnailStore/removeThumbnailPagesForDocument', { documentId: model.documentId, pages: [pageNumber] }, { root: true });
      if (reloadPageOnDelete) {
        const pageCount = getters.document.totalPages;
        dispatch('loadPage', Math.min(pageNumber, pageCount));
      }
    } catch (error) {
      Logger.$log('Error deleting document page', error);
    } finally {
      commit('DELETING', false);
    }
  },
  async loadPageWithJobId({ state, commit, dispatch, rootGetters }, workflowDocumentSearchModel: WorkflowDocumentSearchModel) {
    commit('PAGE_NUMBER', workflowDocumentSearchModel.page);
    commit('PAGE_LOADING');
    commit('SET_IS_CONVERSION_SLOW', false);

    if (workflowDocumentCancelTokenSource) {
      workflowDocumentCancelTokenSource.cancel('New request initiated');
    }
    workflowDocumentCancelTokenSource = axios.CancelToken.source();

    try {
      const document: DocumentModel = ((await ApiConfiguration.apiWithoutNotification().workflowJob.workflowJobDocumentDetail(
        { jobId: workflowDocumentSearchModel.jobId.toString(), JobId: workflowDocumentSearchModel.jobId, Page: workflowDocumentSearchModel.page },
        { cancelToken: workflowDocumentCancelTokenSource.token }
      )) as unknown) as DocumentModel;
      commit('SET_CURRENT_VIEW_CONVERSION_ID', document.viewConversionId);
      try {
        if (document.isBeingConverted === true) {
          try {
            await connectToSignalRHub(state, rootGetters, dispatch);
          } catch (error) {
            Logger.$log('failed to connect to SignalR Hub');
            return;
          }
        }
        await handleGetDocumentResponse(document, commit, dispatch, workflowDocumentSearchModel.page);
      } catch (error) {
        handleGetDocumentError(error, commit, document);
      }
    } catch (error) {
      if (!axios.isCancel(error)) {
        const exceptionResponse = error as MStoreExceptionResponseModel;
        commit('DOCUMENT_LOAD_FAILED', exceptionResponse.notification);
      }
      Logger.$log('Failed to get document');
    }
  },
  setCurrentDocumentBeingViewed({ commit, dispatch }) {
    commit('DOCUMENT_VERSION_BEING_VIEWED', {} as DocumentVersionModel);
    commit('RELATED_DOCUMENT_BEING_VIEWED', {} as RelatedDocumentViewModel);
    dispatch('loadPage', 1);
  },
  async unloadDocument({ commit }) {
    commit('UNLOAD_DOCUMENT');
    reopenClosedSignalRConnection = false;
    await closeConnectionSR();
  },
  downloadPageNeighbours({ state, commit, rootGetters }, documentPageData: DocumentPageDataModel) {
    const maximumPagesToCache = rootGetters['documentViewStore/maximumPagesToCache'];
    if (maximumPagesToCache === 0) {
      return;
    }
    const pageNumbers = DocumentViewUtils.getNeighboringPageNumbers(state.DocumentModel.totalPages, documentPageData.pageNumber, 2);
    for (const pageNumber of pageNumbers) {
      const isPageCached = rootGetters['documentViewStore/isDocumentPageCached']({
        pageNumber,
        documentId: documentPageData.documentId
      } as DocumentPageDataModel);
      if (!isPageCached) {
        commit('PAGE_DOWNLOADED', {
          pageNumber,
          documentData: '',
          documentId: documentPageData.documentId
        } as DocumentPageModel);
        DocumentService.getDocumentAsync(documentPageData.cabinetId, documentPageData.documentId, pageNumber, state.ViewConversionId).then(async (response) => {
          const payload: DocumentModel = response;
          commit('PAGE_DOWNLOADED', {
            pageNumber,
            documentData: payload.documentData,
            documentId: documentPageData.documentId
          } as DocumentPageModel);
        });
      }
    }
  },
  showLoadingSpinner({ state, commit, rootGetters }, showLoadingSpinner: boolean) {
    commit('SHOW_LOADING_SPINNER', showLoadingSpinner);
  },
  setIsConversionSlow({ commit }, isConversionSlow: boolean) {
    commit('SET_IS_CONVERSION_SLOW', isConversionSlow);
  },
  updatePageCount({ state, commit }, change: number) {
    commit('UPDATE_PAGE_COUNT', change);
  },
  retrieveDocumentActions({ commit, getters }) {
    const searchModel = getters.isRelatedDocumentBeingViewed ? getters.relatedDocumentBeingViewed.documentSearchModel : getters.documentSearchModel;
    const lastSearchModel = getters.documentActionsSearchModel;

    if (lastSearchModel?.cabinetId === searchModel?.cabinetId && lastSearchModel?.documentTypeId === searchModel?.documentTypeId && lastSearchModel?.documentId === searchModel?.documentId) {
      return;
    }

    commit('SET_DOCUMENT_ACTIONS', { searchModel, documentActions: {} as DocumentActionsModel });
    DocumentService.getDocumentActionsAsync(searchModel).then((documentActions) => {
      commit('SET_DOCUMENT_ACTIONS', { searchModel, documentActions });
    });
  }
};

async function handleGetDocumentResponse(response: DocumentModel, commit: any, dispatch: Dispatch, pageNumber: number) {
  const payload: DocumentModel = response;
  const documentPageData = {
    documentId: payload.id,
    pageNumber,
    cabinetId: response.cabinetId
  } as DocumentPageDataModel;
  if (payload.hasNotification) {
    const documentErrorModel = {
      document: payload,
      errorMessage: payload.notification
    } as DocumentErrorModel;
    commit('DOCUMENT_LOAD_FAILED', documentErrorModel);
    return;
  }
  commit('DOCUMENT_LOADED', payload);
  if (payload.isBeingConverted) {
    await invokeConvertDocument(payload.viewConversionId);
  } else {
    commit('PAGE_LOADED', {
      pageNumber: documentPageData.pageNumber,
      documentData: payload.documentData,
      documentId: payload.id
    } as DocumentPageModel);
    commit('PAGE_DOWNLOADED', {
      pageNumber: documentPageData.pageNumber,
      documentData: payload.documentData,
      documentId: documentPageData.documentId
    } as DocumentPageModel);
    dispatch('downloadPageNeighbours', documentPageData);
  }
}

function handleGetDocumentError(reason: any, commit: any, document?: DocumentModel) {
  const errorMessage: string = reason.response ? JSON.stringify(reason.response.data) : String(reason);
  const documentErrorModel = document
    ? ({
        document,
        errorMessage
      } as DocumentErrorModel)
    : errorMessage;
  commit('DOCUMENT_LOAD_FAILED', documentErrorModel);
}

// tslint:disable-next-line: no-var-requires
const signalR = require('@microsoft/signalr');
let connection = null as any;
let reopenClosedSignalRConnection = true;
let workflowDocumentCancelTokenSource: CancelTokenSource | null = null;
let documentCancelTokenSource: CancelTokenSource | null = null;

const ViewConversionHubMessageName = 'ViewConversion';
const ViewConversionSucceeededHubMessageName = 'ViewConversionSucceeded';
const ViewConversionFailedHubMessageName = 'ViewConversionFailed';
const ViewConversionTimedOutHubMessageName = 'ViewConversionTimedOut';

async function connectToSignalRHub(state: DocumentViewState, rootGetters: any, dispatch: Dispatch) {
  if (!connection) {
    reopenClosedSignalRConnection = true;
    buildSignalRConnection(rootGetters);
    await startSignalRConnection();
    await subscribeToViewConversionSucceeded(state, dispatch);
    await subscribeToViewConversionFailed(state);
    await subscribeToViewConversionTimedOut(state);
  }
}

function buildSignalRConnection(this: any, rootGetters: any) {
  const baseUrl = getSignalRHubBaseUrl();
  connection = new signalR.HubConnectionBuilder()
    .withUrl(`${baseUrl}/signalrhubs/viewconversion`, {
      accessTokenFactory: () => rootGetters['oidcStore/oidcAccessToken']
    })
    .configureLogging(signalR.LogLevel.Information)
    .build();
}

function getSignalRHubBaseUrl(): string {
  return process.env.NODE_ENV === 'production' ? mstoreSettings.baseUrl : EnvironmentConstants.DevServerBaseUrl;
}

async function startSignalRConnection() {
  try {
    await connection.start();
  } catch (error) {
    Logger.$log(error);
    setTimeout(() => startSignalRConnection(), 5000);
  }
}

async function reconnectOnClose() {
  await connection.onclose(async () => {
    if (reopenClosedSignalRConnection) {
      await startSignalRConnection();
    }
  });
}

async function subscribeToViewConversionSucceeded(this: any, state: DocumentViewState, dispatch: Dispatch) {
  await connection.on(ViewConversionSucceeededHubMessageName, (data: any) => {
    state.ViewConversionId = data;
    if (state.CurrentViewConversionId === data) {
      dispatch('loadPage', 1);
    }
  });
}

async function subscribeToViewConversionFailed(state: DocumentViewState) {
  await subscribeToFailureHubMessage(state, ViewConversionFailedHubMessageName, 'The document failed to be converted');
}

async function subscribeToViewConversionTimedOut(state: DocumentViewState) {
  await subscribeToFailureHubMessage(state, ViewConversionTimedOutHubMessageName, 'The document failed to be converted because the time out period elapsed');
}

async function subscribeToFailureHubMessage(state: DocumentViewState, hubMessageName: string, notificationMessage: string) {
  await connection.on(hubMessageName, (data: any) => {
    state.ViewConversionId = data;
    if (state.CurrentViewConversionId === data) {
      state.ViewConversionId = null;
      state.DocumentModel.notification = notificationMessage;
      state.DocumentModel.hasNotification = true;
      state.DocumentModel.isBeingConverted = false;
      state.hasDocumentError = true;
    }
  });
}

async function invokeConvertDocument(viewConversionId: number) {
  if (connection.state !== signalR.HubConnectionState.Connected) {
    return;
  }
  await connection.invoke(ViewConversionHubMessageName, viewConversionId);
}

async function closeConnectionSR() {
  if (!connection) {
    return;
  }
  if (connection.state === signalR.HubConnectionState.Connected) {
    connection.off(ViewConversionSucceeededHubMessageName);
    connection.off(ViewConversionTimedOutHubMessageName);
    connection.off(ViewConversionFailedHubMessageName);
    await connection.stop();
  }
  connection = null;
}
